import {useSyncExternalStore} from 'react';
import type {SmtRuntimePort, SmtViewModel} from './contracts.ts';
import {createSmtRuntimePort, type SmtRuntimeAdapterConfig} from './smt-runtime-adapter.ts';
import {decorateSmtPaymentProofReviewPort} from './smt-payment-proof-review-port.ts';
import {
  createSmtDineInPort,
  type SmtDineInDispatchResult,
  type SmtDineInRequest,
  type SmtDineInUiIntent,
  type SmtDineInViewModel,
} from './smt-dine-in-port.ts';
import {createSmtAvailabilityPort,type SmtAvailabilityPort} from './smt-availability-port.ts';
import {createSmtBusinessDayPort,type SmtBusinessDayPort} from './smt-business-day-port.ts';
import {createSmtReportingPort,type SmtReportingPort} from './smt-reporting-port.ts';
import {createSmtPrintingPort,type SmtPrintingPort} from './smt-printing-port.ts';
import {createDefaultAndroidRuntimeAdapterConfig} from './smt-device-gate-bootstrap-consumer.ts';
import {createAcceptanceRuntimeBootstrap} from '../../../packages/runtime/acceptance-runtime-provider.ts';

export interface SmtP01DirectWorkOps {
  removeCurrentLine(lineId:string):boolean;
  clearCurrentWork():boolean;
}

export type ConnectedSmtRuntimePort = SmtRuntimePort & SmtAvailabilityPort & SmtBusinessDayPort & SmtReportingPort & SmtPrintingPort & SmtP01DirectWorkOps & {
  readonly readDineIn: (request?: SmtDineInRequest) => Promise<SmtDineInViewModel>;
  readonly dispatchDineIn: (intent: SmtDineInUiIntent, request?: SmtDineInRequest) => Promise<SmtDineInDispatchResult>;
};

declare global {
  interface Window {
    __MORE_FUN_OS_SMT_RUNTIME__?: ConnectedSmtRuntimePort;
    __MORE_FUN_OS_SMT_BOOTSTRAP__?: SmtRuntimeAdapterConfig;
  }
}

export function installSmtRuntime(config:SmtRuntimeAdapterConfig):ConnectedSmtRuntimePort{
  const base=decorateSmtPaymentProofReviewPort(createSmtRuntimePort(config),config);
  const localListeners=new Set<()=>void>();
  const emitLocal=()=>{for(const listener of localListeners)listener();};
  const subscribe:SmtRuntimePort['subscribe']=(listener)=>{localListeners.add(listener);const unsubscribeBase=base.subscribe(listener);return ()=>{localListeners.delete(listener);unsubscribeBase();};};
  const mutationContext=()=>{
    const snapshot=base.getSnapshot().p01.workspace;
    if(!snapshot)return null;
    return {
      workspaceId:snapshot.workspaceId,
      actorId:config.operatorId,
      expectedRevision:snapshot.revision,
      idempotencyKey:config.nextId('idempotency'),
      at:config.now(),
      surface:'smt' as const,
      ...(config.deviceId?.trim()?{deviceId:config.deviceId.trim()}:{}),
    };
  };
  const removeCurrentLine=(lineId:string):boolean=>{
    const context=mutationContext();if(!context)return false;
    const result=config.runtime.smtP01Workspace.removeLine(context,{lineId});
    emitLocal();
    return result.kind==='accepted';
  };
  const clearCurrentWork=():boolean=>{
    let changed=false;
    for(let guard=0;guard<500;guard+=1){
      const snapshot=base.getSnapshot().p01.workspace;
      const line=snapshot?.lines[0];
      if(!snapshot||!line)break;
      const context=mutationContext();if(!context)break;
      const result=config.runtime.smtP01Workspace.removeLine(context,{lineId:line.lineId});
      if(result.kind!=='accepted')break;
      changed=true;
    }
    if(changed)emitLocal();
    return changed;
  };
  const dineIn=createSmtDineInPort({
    surface:config.runtime.smtDineIn,
    operatorId:config.operatorId,
    now:config.now,
    nextId:()=>config.nextId('idempotency'),
  });
  const availability=createSmtAvailabilityPort(config.runtime,{
    operatorId:config.operatorId,
    now:config.now,
    nextIdempotencyKey:()=>config.nextId('idempotency'),
  });
  const businessDay=createSmtBusinessDayPort(config.runtime,{
    operatorId:config.operatorId,
    now:config.now,
    nextIdempotencyKey:()=>config.nextId('idempotency'),
  });
  const reporting=createSmtReportingPort(config.runtime,{
    operatorId:config.operatorId,
    now:config.now,
  });
  const printing=createSmtPrintingPort(config.runtime,{
    operatorId:config.operatorId,
    now:config.now,
    nextPrintIntentId:()=>config.nextId('idempotency'),
  });
  const runtime:ConnectedSmtRuntimePort={
    ...base,
    subscribe,
    removeCurrentLine,
    clearCurrentWork,
    readDineIn:dineIn.read,
    dispatchDineIn:dineIn.dispatch,
    ...availability,
    ...businessDay,
    ...reporting,
    ...printing,
  };
  window.__MORE_FUN_OS_SMT_RUNTIME__=runtime;
  return runtime;
}

function createCloudflareAcceptanceRuntimeAdapterConfig(url:URL):SmtRuntimeAdapterConfig|null{
  const isCloudflarePages=url.protocol==='https:'&&url.hostname.endsWith('.pages.dev');
  if(!isCloudflarePages)return null;
  let sequence=0;
  const result=createAcceptanceRuntimeBootstrap({
    deviceId:'cloudflare-owner-acceptance',
    now:()=>new Date().toISOString(),
    nextId:(kind)=>`cloudflare-owner-acceptance-${kind}-${++sequence}`,
  });
  if(result.kind!=='ready'||result.profile!=='device-gate')return null;
  return {runtime:result.runtime,...result.adapterInputs};
}

export function resolveSmtRuntime(): ConnectedSmtRuntimePort | null {
  if(window.__MORE_FUN_OS_SMT_RUNTIME__)return window.__MORE_FUN_OS_SMT_RUNTIME__;
  if(window.__MORE_FUN_OS_SMT_BOOTSTRAP__)return installSmtRuntime(window.__MORE_FUN_OS_SMT_BOOTSTRAP__);
  const url=new URL(window.location.href);
  const androidRuntime=createDefaultAndroidRuntimeAdapterConfig(url);
  if(androidRuntime)return installSmtRuntime(androidRuntime);
  const cloudflareAcceptanceRuntime=createCloudflareAcceptanceRuntimeAdapterConfig(url);
  return cloudflareAcceptanceRuntime?installSmtRuntime(cloudflareAcceptanceRuntime):null;
}

export function useSmtSnapshot(runtime: SmtRuntimePort): SmtViewModel {
  return useSyncExternalStore(runtime.subscribe, runtime.getSnapshot, runtime.getSnapshot);
}
